import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { DepthLevel, DepthPartner } from "@greenbook/client";
import { useDepth } from "../lib/queries.js";
import { fmtCents, fmtSize } from "../lib/format.js";
import { venueColorMap } from "../lib/venueColors.js";
import { Loading, ErrorState, EmptyState, Badge } from "./ui.js";

/**
 * Orderbook depth for one position — embeddable (contest screen) or full-page.
 * One-sided books (verified live): each partner posts a yes-price ladder. The
 * consolidated book marks which venues sit at each level with color dots
 * (colors shared with the price-history chart) and a hover popup breaks the
 * level down per venue.
 */
export function DepthPanel({
  positionHash,
  title,
  onClose,
  flush = false,
}: {
  positionHash: string;
  title?: string | null;
  /** Present when embedded — renders the close affordance + full-page link. */
  onClose?: () => void;
  /** Fill a terminal pane edge-to-edge instead of rendering as a floating card. */
  flush?: boolean;
}) {
  const depthQ = useDepth(positionHash);
  const partners = depthQ.data?.partners ?? [];

  const colors = useMemo(
    () => venueColorMap(partners.map((p) => p.partner_name ?? p.partner_id)),
    [partners],
  );

  const ageSec = useMemo(() => {
    const t = depthQ.data?.fetched_at;
    if (!t) return null;
    const ms = Date.parse(t);
    return Number.isNaN(ms) ? null : Math.max(0, Math.round((Date.now() - ms) / 1000));
  }, [depthQ.data?.fetched_at, depthQ.dataUpdatedAt]);

  const bests = partners
    .map((p) => p.best_price)
    .filter((v): v is number => typeof v === "number");
  const spreadCents =
    bests.length >= 2 ? (Math.max(...bests) - Math.min(...bests)) * 100 : null;

  const merged = useMemo(() => mergeBook(partners), [partners]);

  return (
    <section
      className={
        flush ? "h-full bg-panel" : "mx-2 mb-4 rounded border border-border-2 bg-panel"
      }
    >
      <div className="flex items-center gap-2 border-b border-border-2 px-3 py-2 lg:px-2 lg:py-1.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-amber">depth</h3>
        {title && (
          <span className="min-w-0 break-words text-xs text-ink lg:text-[11px]" title={positionHash}>
            {title}
          </span>
        )}
        {!flush && (
          <Badge tone="dim" title="each partner posts a one-sided yes-price ladder">
            one-sided
          </Badge>
        )}
        {spreadCents != null && (
          <span className="text-[10px] text-faint" title="cross-venue best-price differential">
            Δ <span className={`nums ${spreadCents >= 1 ? "text-amber" : "text-dim"}`}>{spreadCents.toFixed(1)}¢</span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {ageSec != null && (
            <span className={`nums text-[10px] ${ageSec > 30 ? "text-amber" : "text-faint"}`}>
              {ageSec}s
            </span>
          )}
          <button
            onClick={() => depthQ.refetch()}
            disabled={depthQ.isFetching}
            className="rounded border border-border-2 px-2.5 py-1.5 text-[11px] text-ink hover:bg-raised disabled:opacity-50 lg:px-1.5 lg:py-0.5 lg:text-[10px]"
          >
            {depthQ.isFetching ? "…" : "refresh"}
          </button>
          {onClose && (
            <>
              <Link
                to={`/depth/${encodeURIComponent(positionHash)}`}
                className="-m-1.5 shrink-0 p-1.5 text-xs text-dim hover:text-amber lg:m-0 lg:p-0 lg:text-[10px]"
                title="open full-page depth"
              >
                ⤢
              </Link>
              <button onClick={onClose} className="-m-1.5 p-2 text-sm text-dim hover:text-ink lg:m-0 lg:px-1 lg:py-0 lg:text-[11px]">
                ✕
              </button>
            </>
          )}
        </div>
      </div>

      {/* per-venue best strip with the shared color dots */}
      {partners.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border px-3 py-2 text-xs lg:gap-y-1 lg:px-2 lg:py-1 lg:text-[11px]">
          {partners.map((p) => {
            const name = p.partner_name ?? p.partner_id;
            return (
              <span key={p.partner_id} className="flex items-center gap-1.5">
                <VenueDot color={colors.get(name)} />
                <span className="text-dim">{name}</span>
                <span className="nums text-ink">{fmtCents(p.best_price)}</span>
                <span className="nums text-[10px] text-faint">{fmtSize(p.total_available)}</span>
              </span>
            );
          })}
        </div>
      )}

      <div className="p-3 lg:p-2">
        {depthQ.isLoading ? (
          <Loading label="loading depth…" />
        ) : depthQ.isError ? (
          <ErrorState error={depthQ.error} onRetry={() => depthQ.refetch()} />
        ) : merged.rows.length === 0 ? (
          <EmptyState title="No depth posted here right now." />
        ) : (
          <MergedBook rows={merged.rows} maxCum={merged.maxCum} colors={colors} />
        )}
      </div>
    </section>
  );
}

export function VenueDot({ color, size = 7 }: { color?: string; size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, background: color ?? "#8b93a7" }}
    />
  );
}

interface MergedRow {
  price: number;
  available: number;
  cum: number;
  /** venue name → size at this level */
  byVenue: [string, number][];
}

/** Consolidate every partner's levels into one price-keyed book (size summed),
 * tracking the per-venue contribution at each level for the hover popup. */
export function mergeBook(partners: DepthPartner[]): { rows: MergedRow[]; maxCum: number } {
  const byPrice = new Map<number, Map<string, number>>();
  for (const p of partners) {
    const name = p.partner_name ?? p.partner_id;
    for (const lvl of p.levels ?? []) {
      if (typeof lvl.price !== "number") continue;
      const level = byPrice.get(lvl.price) ?? new Map<string, number>();
      level.set(name, (level.get(name) ?? 0) + (lvl.available ?? 0));
      byPrice.set(lvl.price, level);
    }
  }
  const sorted = [...byPrice.entries()].sort((a, b) => b[0] - a[0]); // price desc
  let running = 0;
  const rows: MergedRow[] = sorted.map(([price, venues]) => {
    const byVenue = [...venues.entries()].sort((a, b) => b[1] - a[1]); // biggest first
    const available = byVenue.reduce((n, [, amt]) => n + amt, 0);
    running += available;
    return { price, available, cum: running, byVenue };
  });
  return { rows, maxCum: running || 1 };
}

const VISIBLE_LEVELS = 16;

function MergedBook({
  rows,
  maxCum,
  colors,
}: {
  rows: MergedRow[];
  maxCum: number;
  colors: Map<string, string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, VISIBLE_LEVELS);
  return (
    <div className="rounded border border-border-2 bg-panel-2">
      <div className="grid grid-cols-[3.5rem_4rem_4rem_minmax(0,1fr)] gap-1 border-b border-border px-2 py-1.5 text-[10px] uppercase text-faint lg:grid-cols-[3.5rem_4.5rem_4.5rem_minmax(0,1fr)] lg:py-1">
        <span className="text-right">price</span>
        <span className="text-right">size</span>
        <span className="text-right">cum</span>
        <span className="pl-1">venues</span>
      </div>
      <div>
        {visible.map((r) => (
          <LevelRow key={r.price} row={r} maxCum={maxCum} colors={colors} />
        ))}
      </div>
      {rows.length > VISIBLE_LEVELS && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full border-t border-border px-2 py-2 text-center text-[11px] text-dim hover:text-amber lg:py-1 lg:text-[10px]"
        >
          {expanded ? "show fewer levels" : `+ ${rows.length - VISIBLE_LEVELS} more levels`}
        </button>
      )}
    </div>
  );
}

function LevelRow({
  row,
  maxCum,
  colors,
}: {
  row: MergedRow;
  maxCum: number;
  colors: Map<string, string>;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative grid grid-cols-[3.5rem_4rem_4rem_minmax(0,1fr)] items-center gap-1 px-2 py-1.5 text-[13px] hover:bg-raised/60 lg:grid-cols-[3.5rem_4.5rem_4.5rem_minmax(0,1fr)] lg:py-0.5 lg:text-[11px]"
    >
      <span
        className="absolute inset-y-0 right-0 bg-amber/10"
        style={{ width: `${(row.cum / maxCum) * 100}%` }}
      />
      <span className="nums relative z-10 text-right text-ink">{fmtCents(row.price)}</span>
      <span className="nums relative z-10 text-right text-dim">{fmtSize(row.available)}</span>
      <span className="nums relative z-10 text-right text-faint">{fmtSize(row.cum)}</span>

      {/* venue presence dots, sized share implied by order (biggest first) */}
      <span className="relative z-10 flex min-w-0 items-center gap-1 pl-1">
        {row.byVenue.map(([name]) => (
          <VenueDot key={name} color={colors.get(name)} />
        ))}
        <span className="ml-0.5 min-w-0 break-words text-[10px] text-faint">
          {row.byVenue.length === 1 ? row.byVenue[0][0] : `×${row.byVenue.length}`}
        </span>
      </span>

      {/* hover popup: per-venue amounts at this level */}
      {hover && (
        <div className="absolute right-2 top-full z-20 mt-0.5 min-w-[13rem] rounded border border-border-2 bg-panel px-2 py-1.5 shadow-lg">
          <div className="mb-1 flex justify-between text-[10px] uppercase text-faint">
            <span>{fmtCents(row.price)} level</span>
            <span className="nums">{fmtSize(row.available)}</span>
          </div>
          {row.byVenue.map(([name, amt]) => (
            <div key={name} className="flex items-center gap-1.5 py-0.5 text-[11px]">
              <VenueDot color={colors.get(name)} />
              <span className="text-dim">{name}</span>
              <span className="nums ml-auto text-ink">{fmtSize(amt)}</span>
              <span className="nums w-10 text-right text-[10px] text-faint">
                {row.available > 0 ? `${Math.round((amt / row.available) * 100)}%` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PartnerLadder({
  partner,
  color,
}: {
  partner: DepthPartner;
  color?: string;
}) {
  const levels = [...(partner.levels ?? [])].sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
  const cumTotal = levels.reduce((n, l) => n + (l.available ?? 0), 0);
  let running = 0;
  const rows = levels.map((l: DepthLevel) => {
    running += l.available ?? 0;
    return { level: l, cum: running };
  });
  const maxCum = running || 1;

  return (
    <div className="w-full shrink-0 rounded border border-border bg-panel-2 sm:w-64">
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
        <VenueDot color={color} />
        <span className="text-xs font-semibold text-ink">
          {partner.partner_name ?? partner.partner_id}
        </span>
        <span className="nums ml-auto text-[10px] text-dim">best {fmtCents(partner.best_price)}</span>
      </div>
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-1 border-b border-border px-2 py-1 text-[10px] uppercase text-faint">
        <span className="text-right">price</span>
        <span className="text-right">size</span>
        <span className="text-right">cum</span>
      </div>
      <div>
        {rows.map(({ level, cum }, i) => (
          <div key={i} className="relative grid grid-cols-[1fr_1fr_1fr] gap-1 px-2 py-0.5 text-[11px]">
            <span
              className="absolute inset-y-0 right-0"
              style={{
                width: `${(cum / maxCum) * 100}%`,
                background: color ? `${color}1f` : "rgba(76,159,254,0.1)",
              }}
            />
            <span className="nums relative z-10 text-right text-ink">{fmtCents(level.price)}</span>
            <span className="nums relative z-10 text-right text-dim">{fmtSize(level.available)}</span>
            <span className="nums relative z-10 text-right text-faint">{fmtSize(cum)}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-border px-2 py-1 text-right text-[10px] text-faint">
        total {fmtSize(partner.total_available ?? cumTotal)}
      </div>
    </div>
  );
}
